<!-- =========================================================================================
    File Name: Home.vue
    Description: Dashboard CFC Solutions v1.8
========================================================================================== -->

<template>
  <div class="home">

    <vs-button color="primary" type="border" icon-pack="far" icon="fa-download" class="download__button mb-8" @click="openModal()">
      Rapport d'activité
    </vs-button>

    <ReportModal :show-modal="reportModal" @close-modal="closeModal"/>

    <div v-if="kpiLoading" class="kpi-loading">
      <div class="kpi-spinner"></div>
    </div>

    <div v-else-if="kpiError" class="kpi-error">
      ⚠ Impossible de charger les données du tableau de bord
    </div>

    <template v-else>

      <div class="kpi-periode">Sur les 30 derniers jours</div>

      <section class="vx-row items-stretch dashboard-cards">

        <!-- CARD 1 -->
        <div class="vx-col flex items-stretch w-full sm:w-1/2 md:w-1/2 lg:w-1/3 xl:w-1/3 mb-base">
          <vx-card class="kpi-card">
            <div class="kpi-inner">
              <div class="kpi-top">
                <div class="kpi-label">Factures déposées<br>sur les 30 derniers jours</div>
                <div class="kpi-ico kpi-ico--blue"><i class="fal fa-file-plus"></i></div>
              </div>
              <div class="kpi-value">{{ kpi.factures_30j }}</div>
              <div class="kpi-spark"><canvas ref="sp1"></canvas></div>
              <div class="kpi-foot">
                <span v-if="kpi.factures_prev" class="kpi-badge" :class="kpi.factures_30j >= kpi.factures_prev ? 'kpi-up' : 'kpi-down'">
                  {{ kpi.factures_30j >= kpi.factures_prev ? '▲' : '▼' }} {{ trendPct(kpi.factures_30j, kpi.factures_prev) }}&thinsp;%
                </span>
                <span class="kpi-foot-txt">vs période préc.</span>
              </div>
            </div>
          </vx-card>
        </div>

        <!-- CARD 2 -->
        <div class="vx-col flex items-stretch w-full sm:w-1/2 md:w-1/2 lg:w-1/3 xl:w-1/3 mb-base">
          <vx-card class="kpi-card">
            <div class="kpi-inner">
              <div class="kpi-top">
                <div class="kpi-label">
                  Délai moyen confié
                  <span class="kpi-info" @click="showInfo = !showInfo"><i class="fal fa-info-circle"></i></span>
                  <div v-if="showInfo" class="kpi-tooltip">
                    Moyenne de (date échéance − date dépôt) sur les factures déposées ces 30 derniers jours.<br>
                    <strong>Valeur positive</strong> : déposées avant échéance.<br>
                    <strong>Valeur négative</strong> : déposées après échéance.
                  </div>
                </div>
                <div class="kpi-ico kpi-ico--orange"><i class="fal fa-clock"></i></div>
              </div>
              <div class="kpi-value">{{ kpi.echeance_fmt }}<span v-if="kpi.echeance_fmt !== 'NS'" class="kpi-unit">j</span></div>
              <div class="kpi-spark"><canvas ref="sp2"></canvas></div>
              <div class="kpi-foot">
                <span v-if="kpi.echeance_fmt === 'NS'" class="kpi-ns">Données non significatives pour ce compte</span>
                <template v-else>
                  <span v-if="kpi.echeance_prev" class="kpi-badge" :class="kpi.echeance_moy <= kpi.echeance_prev ? 'kpi-up' : 'kpi-down'">
                    {{ kpi.echeance_moy <= kpi.echeance_prev ? '▼' : '▲' }} {{ trendPct(kpi.echeance_moy, kpi.echeance_prev) }}&thinsp;%
                  </span>
                  <span class="kpi-foot-txt">vs période préc.</span>
                </template>
              </div>
            </div>
          </vx-card>
        </div>

        <!-- CARD 3 -->
        <div class="vx-col flex items-stretch w-full sm:w-1/2 md:w-1/2 lg:w-1/3 xl:w-1/3 mb-base">
          <vx-card class="kpi-card">
            <div class="kpi-inner">
              <div class="kpi-top">
                <div class="kpi-label">Factures déposées (30j)<br>— état de traitement</div>
                <div class="kpi-ico kpi-ico--red"><i class="fal fa-chart-pie"></i></div>
              </div>
              <div class="kpi-donut-body">
                <div class="kpi-donut-wrap">
                  <canvas ref="donut"></canvas>
                  <div class="kpi-donut-center">
                    <div class="kpi-donut-val">{{ kpi.total }}</div>
                    <div class="kpi-donut-lbl">factures</div>
                  </div>
                </div>
                <div class="kpi-groups">
                  <div class="kpi-group">
                    <div class="kpi-group-header">
                      <span class="kpi-group-dot" style="background:#E8401C"></span>
                      <span class="kpi-group-label">En cours</span>
                      <span class="kpi-group-count">{{ kpi.groupe_a.count }}</span>
                    </div>
                    <div class="kpi-group-montant">{{ formatMontant(kpi.groupe_a.montant) }}</div>
                    <div class="kpi-group-pct">{{ kpi.groupe_a.pct }}&thinsp;%</div>
                  </div>
                  <div class="kpi-group-sep"></div>
                  <div class="kpi-group">
                    <div class="kpi-group-header">
                      <span class="kpi-group-dot" style="background:#22C55E"></span>
                      <span class="kpi-group-label">Terminées / réglées</span>
                      <span class="kpi-group-count">{{ kpi.groupe_b.count }}</span>
                    </div>
                    <div class="kpi-group-montant kpi-group-montant--green">{{ formatMontant(kpi.groupe_b.montant) }}</div>
                    <div class="kpi-group-pct">{{ kpi.groupe_b.pct }}&thinsp;%</div>
                  </div>
                  <div class="kpi-group-sep"></div>
                  <div class="kpi-group">
                    <div class="kpi-group-header">
                      <span class="kpi-group-dot" style="background:#F07830"></span>
                      <span class="kpi-group-label">À intégrer</span>
                      <span class="kpi-group-count">{{ kpi.a_integrer.count }}</span>
                    </div>
                    <div class="kpi-group-montant kpi-group-montant--orange">{{ formatMontant(kpi.a_integrer.montant) }}</div>
                    <div class="kpi-group-pct">{{ kpi.a_integrer.pct }}&thinsp;%</div>
                  </div>
                </div>
              </div>
              <div v-if="kpi.ne_pas_traiter.count > 0" class="kpi-nt">
                <span class="kpi-nt-dot"></span>
                Ne pas traiter : {{ kpi.ne_pas_traiter.count }} facture{{ kpi.ne_pas_traiter.count > 1 ? 's' : '' }}
                — {{ formatMontant(kpi.ne_pas_traiter.montant) }}
              </div>
            </div>
          </vx-card>
        </div>

      </section>

      <!-- BLOC INFÉRIEUR AVEC SWITCH -->
      <vx-card class="bottom-card mt-base">

        <div class="bottom-header">
          <div class="bottom-title">
            <i :class="['fal', activeView === 'projection' ? 'fa-chart-bar' : 'fa-balance-scale']"></i>
            {{ activeView === 'projection' ? 'Projection des encaissements — 4 prochaines semaines' : 'Balance âgée des créances en traitement' }}
          </div>
          <div class="view-switch">
            <button :class="['switch-btn', activeView === 'projection' ? 'active' : '']" @click="setView('projection')">
              <i class="fal fa-chart-bar"></i> Projection
            </button>
            <button :class="['switch-btn', activeView === 'balance' ? 'active' : '']" @click="setView('balance')">
              <i class="fal fa-balance-scale"></i> Balance âgée
            </button>
          </div>
        </div>

        <!-- VUE PROJECTION -->
        <div v-if="activeView === 'projection'">
          <div class="balance-legend mb-4">
            <span class="legend-dot" style="background:#22C55E"></span> Paiements annoncés
            <span class="legend-dot ml" style="background:#F5C842"></span> Échéances sans accord
          </div>
          <div v-if="balanceLoading" class="kpi-loading" style="height:120px"><div class="kpi-spinner"></div></div>
          <div v-else-if="balance.semaines && balance.semaines.length" class="balance-bars">
            <div v-for="s in balance.semaines" :key="s.label" class="balance-col">
              <div class="balance-total-amt">{{ formatMontantK(s.total_montant) }}</div>
              <div class="balance-stack">
                <!-- Montant dans la barre, nb en dessous -->
                <div class="balance-seg seg-green" :style="{ height: barHeightProj(s.paiements_montant) + 'px' }">
                  <span v-if="s.paiements_montant > 0" class="seg-val">{{ formatMontantK(s.paiements_montant) }}</span>
                </div>
                <div class="balance-seg seg-yellow" :style="{ height: barHeightProj(s.echeances_montant) + 'px' }">
                  <span v-if="s.echeances_montant > 0" class="seg-val">{{ formatMontantK(s.echeances_montant) }}</span>
                </div>
              </div>
              <div class="balance-label">{{ s.label }}</div>
              <!-- Volumes sous le graphique -->
              <div class="balance-detail">
                <div class="detail-line"><span class="detail-dot" style="background:#22C55E"></span>{{ s.paiements_nb }} facture{{ s.paiements_nb > 1 ? 's' : '' }}</div>
                <div class="detail-line"><span class="detail-dot" style="background:#F5C842"></span>{{ s.echeances_nb }} facture{{ s.echeances_nb > 1 ? 's' : '' }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- VUE BALANCE ÂGÉE -->
        <div v-if="activeView === 'balance'">
          <div v-if="ageeLoading" class="kpi-loading" style="height:120px"><div class="kpi-spinner"></div></div>
          <div v-else-if="balanceAgee.tranches && balanceAgee.tranches.length" class="balance-bars">
            <div v-for="t in balanceAgee.tranches" :key="t.label" class="balance-col">
              <div class="balance-total-amt">{{ formatMontantK(t.montant) }}</div>
              <div class="balance-stack">
                <div class="balance-seg" :style="{ height: barHeightAgee(t.montant) + 'px', background: t.color }">
                  <span v-if="t.nb > 0" class="seg-val">{{ t.nb }}</span>
                </div>
              </div>
              <div class="balance-label">{{ t.label }}</div>
            </div>
          </div>
        </div>

      </vx-card>

    </template>
  </div>
</template>

<script>
import ReportModal from '../components/ReportModal.vue'
import Chart from 'chart.js'

export default {
  name: 'Home',
  components: { ReportModal },

  data () {
    return {
      reportModal: false,
      kpiLoading: true,
      kpiError: false,
      showInfo: false,
      activeView: 'projection',
      balanceLoading: false,
      ageeLoading: false,
      charts: [],
      kpi: {
        factures_30j: 0, factures_prev: 0,
        echeance_moy: 0, echeance_prev: 0, echeance_fmt: 'NS',
        total: 0,
        groupe_a:      { count: 0, montant: 0, pct: '0.0' },
        groupe_b:      { count: 0, montant: 0, pct: '0.0' },
        a_integrer:    { count: 0, montant: 0, pct: '0.0' },
        ne_pas_traiter:{ count: 0, montant: 0 },
        sparkline_factures: [], sparkline_echeance: [],
      },
      balance:     { semaines: [] },
      balanceAgee: { tranches: [] },
    }
  },

  mounted () {
    this.loadKpi()
    this.loadBalance()
  },

  methods: {

    openModal () { this.reportModal = true },
    closeModal () { this.reportModal = false },

    setView (view) {
      this.activeView = view
      if (view === 'balance' && !this.balanceAgee.tranches.length) {
        this.loadBalanceAgee()
      }
    },

    async loadKpi () {
      this.kpiLoading = true
      this.kpiError = false
      try {
        const { data } = await this.$http.get('/kpi')
        this.buildKpi(data)
      } catch (e) {
        console.error('[KPI]', e)
        this.kpiError = true
      } finally {
        this.kpiLoading = false
        this.$nextTick(() => this.drawCharts())
      }
    },

    async loadBalance () {
      this.balanceLoading = true
      try {
        const { data } = await this.$http.get('/balance')
        this.balance = data
      } catch (e) { console.error('[Balance]', e) }
      finally { this.balanceLoading = false }
    },

    async loadBalanceAgee () {
      this.ageeLoading = true
      try {
        const { data } = await this.$http.get('/balance_agee')
        this.balanceAgee = data
      } catch (e) { console.error('[BalanceAgee]', e) }
      finally { this.ageeLoading = false }
    },

    buildKpi (d) {
      const moy = d.echeance_moy || 0
      const cA  = d.groupe_a ? d.groupe_a.count : 0
      const cB  = d.groupe_b ? d.groupe_b.count : 0
      const cAI = d.a_integrer ? d.a_integrer.count : 0
      const total = cA + cB + cAI
      const pct = (v) => total > 0 ? ((v / total) * 100).toFixed(1) : '0.0'
      this.kpi = {
        factures_30j:    d.factures_30j  || 0,
        factures_prev:   d.factures_prev || 0,
        echeance_moy:    moy,
        echeance_prev:   d.echeance_prev || 0,
        echeance_fmt:    moy < -300 ? 'NS' : Number(moy).toFixed(2).replace('.', ','),
        total,
        groupe_a:      { ...(d.groupe_a || {}),      pct: pct(cA)  },
        groupe_b:      { ...(d.groupe_b || {}),      pct: pct(cB)  },
        a_integrer:    { ...(d.a_integrer || {}),    pct: pct(cAI) },
        ne_pas_traiter: d.ne_pas_traiter || { count: 0, montant: 0 },
        sparkline_factures: Array.isArray(d.sparkline_factures) ? d.sparkline_factures : [],
        sparkline_echeance: Array.isArray(d.sparkline_echeance) ? d.sparkline_echeance : [],
      }
    },

    barHeightProj (montant) {
      if (!this.balance.semaines || !this.balance.semaines.length) return 0
      const max = Math.max(...this.balance.semaines.map(s => s.total_montant))
      return max === 0 ? 0 : Math.max(4, Math.round((montant / max) * 120))
    },

    barHeightAgee (montant) {
      if (!this.balanceAgee.tranches || !this.balanceAgee.tranches.length) return 0
      const max = Math.max(...this.balanceAgee.tranches.map(t => t.montant))
      return max === 0 ? 0 : Math.max(4, Math.round((montant / max) * 120))
    },

    formatMontant (val) {
      return Number(val || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
    },

    formatMontantK (val) {
      if (!val) return '0 €'
      if (val >= 1000000) return (val / 1000000).toFixed(1).replace('.', ',') + ' M€'
      if (val >= 1000) return Math.round(val / 1000) + ' k€'
      return Math.round(val) + ' €'
    },

    trendPct (val, prev) {
      if (!prev || prev === 0) return 0
      return Math.abs(Math.round((val - prev) / prev * 100))
    },

    drawCharts () {
      this.charts.forEach(c => { try { c.destroy() } catch (e) {} })
      this.charts = []
      if (this.$refs.sp1)   this.charts.push(this.makeSparkline(this.$refs.sp1, this.kpi.sparkline_factures, '#5BB8D4'))
      if (this.$refs.sp2)   this.charts.push(this.makeSparkline(this.$refs.sp2, this.kpi.sparkline_echeance, '#F07830'))
      if (this.$refs.donut) this.charts.push(this.makeDonut(this.$refs.donut))
    },

    makeSparkline (canvas, data, color) {
      return new Chart(canvas, {
        type: 'line',
        data: {
          labels: data.map((_, i) => i),
          datasets: [{ data, borderColor: color, borderWidth: 1.5, pointRadius: 0, lineTension: 0.45, fill: true, backgroundColor: color + '18' }]
        },
        options: {
          responsive: true, maintainAspectRatio: false,
          legend: { display: false }, tooltips: { enabled: false },
          scales: { xAxes: [{ display: false }], yAxes: [{ display: false }] },
          animation: { duration: 700 }
        }
      })
    },

    makeDonut (canvas) {
      const a  = this.kpi.groupe_a.count
      const b  = this.kpi.groupe_b.count
      const ai = this.kpi.a_integrer.count
      if (a === 0 && b === 0 && ai === 0) return null
      return new Chart(canvas, {
        type: 'doughnut',
        data: {
          labels: ['En cours', 'Terminées / réglées', 'À intégrer'],
          datasets: [{ data: [a, b, ai], backgroundColor: ['#E8401C', '#22C55E', '#F07830'], borderWidth: 2, borderColor: '#ffffff', hoverBorderWidth: 3 }]
        },
        options: {
          responsive: true, maintainAspectRatio: false, cutoutPercentage: 72,
          legend: { display: false }, animation: { duration: 700 }
        }
      })
    },
  }
}
</script>

<style scoped lang="scss">
.kpi-loading { display: flex; align-items: center; justify-content: center; height: 200px; }
.kpi-spinner { width: 28px; height: 28px; border: 3px solid rgba(0,0,0,.07); border-top-color: #E8401C; border-radius: 50%; animation: kpi-spin .7s linear infinite; }
@keyframes kpi-spin { to { transform: rotate(360deg); } }
.kpi-error { padding: 16px 20px; color: #C0360F; background: #FDEAE5; border-radius: 8px; font-size: 13px; }
.kpi-periode { font-size: 11px; font-weight: 600; color: #7A90A4; text-transform: uppercase; letter-spacing: .08em; margin-bottom: 12px; }
.kpi-card { width: 100%; }
.kpi-inner { display: flex; flex-direction: column; height: 100%; min-height: 220px; }
.kpi-top { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 10px; }
.kpi-label { font-size: 11.5px; color: #7A90A4; line-height: 1.5; position: relative; }
.kpi-info { display: inline-flex; align-items: center; margin-left: 4px; cursor: pointer; color: #5BB8D4; font-size: 12px; vertical-align: middle; }
.kpi-tooltip { position: absolute; top: 22px; left: 0; z-index: 100; background: #1A2A3A; color: #fff; font-size: 10.5px; line-height: 1.6; padding: 8px 12px; border-radius: 8px; width: 260px; box-shadow: 0 4px 12px rgba(0,0,0,.2); }
.kpi-tooltip strong { color: #93D0E0; }
.kpi-ico { width: 30px; height: 30px; border-radius: 7px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 13px; }
.kpi-ico--blue   { background: #E5F4FB; color: #3AAFD0; }
.kpi-ico--orange { background: #FEF0E7; color: #E8720A; }
.kpi-ico--red    { background: #FDEAE5; color: #D83A18; }
.kpi-value { font-size: clamp(30px, 3.5vw, 46px); font-weight: 200; letter-spacing: -.02em; line-height: 1; color: #1A2A3A; margin-bottom: 4px; }
.kpi-unit { font-size: .4em; font-weight: 400; color: #7A90A4; margin-left: 2px; }
.kpi-ns { font-size: 10px; color: #7A90A4; font-style: italic; }
.kpi-spark { flex: 1; min-height: 50px; max-height: 60px; margin: 8px 0; position: relative; }
.kpi-spark canvas { width: 100% !important; height: 100% !important; }
.kpi-foot { padding-top: 8px; border-top: 1px solid rgba(0,0,0,.06); font-size: 10.5px; color: #7A90A4; min-height: 24px; }
.kpi-badge { display: inline-flex; align-items: center; border-radius: 20px; padding: 2px 7px; font-size: 10.5px; font-weight: 600; margin-right: 4px; }
.kpi-up   { background: #DCFCE7; color: #15803D; }
.kpi-down { background: #FEE2E2; color: #B91C1C; }
.kpi-foot-txt { font-size: 10.5px; color: #7A90A4; }
.kpi-donut-body { display: flex; gap: 14px; align-items: center; flex: 1; min-height: 0; margin-top: 8px; }
.kpi-donut-wrap { position: relative; flex-shrink: 0; width: 110px; height: 110px; }
.kpi-donut-center { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; pointer-events: none; }
.kpi-donut-val { font-size: 20px; font-weight: 300; color: #1A2A3A; line-height: 1; }
.kpi-donut-lbl { font-size: 8px; color: #7A90A4; text-align: center; margin-top: 2px; }
.kpi-groups { flex: 1; display: flex; flex-direction: column; gap: 0; justify-content: center; }
.kpi-group { padding: 4px 0; }
.kpi-group-header { display: flex; align-items: center; gap: 6px; margin-bottom: 1px; }
.kpi-group-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.kpi-group-label { flex: 1; font-size: 10.5px; font-weight: 500; color: #1A2A3A; }
.kpi-group-count { font-size: 15px; font-weight: 300; color: #1A2A3A; }
.kpi-group-montant { font-size: 10px; font-weight: 600; color: #1A2A3A; padding-left: 14px; }
.kpi-group-montant--green  { color: #15803D; }
.kpi-group-montant--orange { color: #C05A10; }
.kpi-group-pct { font-size: 9px; color: #7A90A4; padding-left: 14px; }
.kpi-group-sep { border: none; border-top: 1px solid rgba(0,0,0,.06); margin: 3px 0; }
.kpi-nt { margin-top: 10px; padding-top: 8px; border-top: 1px solid rgba(0,0,0,.06); font-size: 10px; color: #7A90A4; display: flex; align-items: center; gap: 5px; }
.kpi-nt-dot { width: 6px; height: 6px; border-radius: 50%; background: #94A3B8; flex-shrink: 0; }

/* BLOC INFÉRIEUR */
.bottom-card { width: 100%; margin-top: 0.5rem; }
.bottom-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; flex-wrap: wrap; gap: 10px; }
.bottom-title { font-size: 13px; font-weight: 600; color: #1A2A3A; display: flex; align-items: center; gap: 8px; }
.bottom-title i { color: #7A90A4; }
.view-switch { display: flex; border: 1px solid rgba(0,0,0,.1); border-radius: 8px; overflow: hidden; }
.switch-btn { font-size: 11px; padding: 5px 14px; cursor: pointer; color: #7A90A4; background: transparent; border: none; display: flex; align-items: center; gap: 5px; transition: all .2s; }
.switch-btn:hover { color: #1A2A3A; }
.switch-btn.active { background: #1A2A3A; color: #fff; }
.switch-btn i { font-size: 11px; }
.balance-legend { display: flex; align-items: center; gap: 8px; font-size: 11px; color: #7A90A4; }
.legend-dot { width: 10px; height: 10px; border-radius: 2px; display: inline-block; }
.legend-dot.ml { margin-left: 12px; }
.balance-bars { display: flex; gap: 16px; align-items: flex-end; }
.balance-col { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 4px; }
.balance-total-amt { font-size: 12px; font-weight: 600; color: #1A2A3A; text-align: center; margin-bottom: 4px; }
.balance-stack { display: flex; flex-direction: column; width: 100%; align-items: center; gap: 2px; }
.balance-seg { width: 80%; border-radius: 3px; display: flex; align-items: center; justify-content: center; min-height: 4px; transition: height .4s ease; }
.seg-green  { background: #22C55E; }
.seg-yellow { background: #F5C842; }
.seg-val { font-size: 10px; color: #fff; font-weight: 600; text-align: center; }
.balance-label { font-size: 11px; color: #7A90A4; text-align: center; margin-top: 6px; border-top: 1px solid rgba(0,0,0,.06); padding-top: 6px; width: 100%; }
.balance-detail { display: flex; flex-direction: column; gap: 2px; margin-top: 4px; width: 100%; }
.detail-line { display: flex; align-items: center; justify-content: center; gap: 4px; font-size: 10px; color: #7A90A4; }
.detail-dot { width: 7px; height: 7px; border-radius: 2px; flex-shrink: 0; }
.mb-4 { margin-bottom: 1rem; }
</style>
